import { type WithChildren } from '@dynatrace/strato-components/core';

import { safeGet } from '../../core/utils/safe-get.js';
import { TimeseriesDataPointAllTypes } from '../components/displays/shared/types.js';
import { TimeseriesFilteredDataContext } from '../context/TimeseriesFilteredData.context.js';
import { useFilteredSeriesIdsState } from '../hooks/store/selectors.js';
import { useBuildFilteredMetadata } from '../hooks/useBuildFilteredMetadata.js';
import { useTimeseriesData } from '../hooks/useTimeseriesData.js';
import { TimeseriesBandSlotProps } from '../types/timeseries-band.js';
import { TimeseriesVariantSlotProps } from '../types/timeseries.js';
import {
  getDatapointTime,
  mergeTimeseriesFromVariants,
} from '../utils/timeseries.js';

function binarySearchOrder(
  a: TimeseriesDataPointAllTypes,
  b: TimeseriesDataPointAllTypes,
) {
  return getDatapointTime(a.start, a.end) - getDatapointTime(b.start, b.end);
}

export const TimeseriesFilteredDataProvider = ({ children }: WithChildren) => {
  const {
    dataPerVariant,
    seriesMap: initialSeriesMap,
    valueRepPerVariant,
    units,
  } = useTimeseriesData();
  const filteredSeriesIds = useFilteredSeriesIdsState();

  const hasId = (
    series: TimeseriesVariantSlotProps | TimeseriesBandSlotProps,
  ) => filteredSeriesIds.has(safeGet(initialSeriesMap, series).id);

  const filteredTimeseriesPerVariant =
    filteredSeriesIds.size > 0
      ? {
          ...dataPerVariant,
          line: dataPerVariant['line'].filter(hasId),
          bar: dataPerVariant['bar'].filter(hasId),
          area: dataPerVariant['area'].filter(hasId),
          band: dataPerVariant['band'].filter(hasId),
        }
      : dataPerVariant;

  const {
    seriesMap,
    datapointsMap,
    filteredUnits,
    filteredTimeseries,
    originalFilteredUnits,
  } = useBuildFilteredMetadata(
    filteredTimeseriesPerVariant,
    valueRepPerVariant,
    initialSeriesMap,
    units,
  );

  const data = mergeTimeseriesFromVariants(filteredTimeseries);
  const allDatapoints = data
    .flatMap((series) => series.datapoints as TimeseriesDataPointAllTypes[])
    // Sorting the array will allow performe the binary search on hover
    .sort(binarySearchOrder);

  return (
    <TimeseriesFilteredDataContext.Provider
      value={{
        data,
        dataPerVariant: filteredTimeseries,
        seriesMap,
        datapointsMap,
        units: filteredUnits,
        originalUnits: originalFilteredUnits,
        allDatapoints,
      }}
    >
      {children}
    </TimeseriesFilteredDataContext.Provider>
  );
};
